/**
 * Pets — companions that follow the hero, level up, and contribute in different
 * ways. Two kinds of roles:
 *   • ACTIVE roles do something each frame (handled in Pet.js):
 *       collector — magnetizes nearby loot and auto-collects it
 *       hunter    — looses a bolt at the nearest enemy on a cooldown
 *   • PASSIVE roles buff the hero via `grants(level)` (summed into the player's
 *     modifiers alongside research):
 *       warden    — +max health        channeler — +max mana & mana regen
 *       duelist   — +crit chance        forager   — +loot fortune
 *
 * `unlockDepth` = deepest floor you must reach before it can be tamed. The Wisp
 * is the starter pet, unlocked from the beginning.
 */
export const PETS = [
  { id: "wisp", name: "Arcane Wisp", model: null, tint: 0xc77dff, role: "collector", utility: true, unlockDepth: 1,
    desc: "A mote of the Vault's memory. Pulls nearby loot toward you and lights the dark." },

  { id: "ghost", name: "Vault Shade", model: "creatures/ghost", role: "channeler", unlockDepth: 2,
    desc: "Channels arcane essence back to you — more max mana and faster regen.",
    grants: (l) => ({ mana: 20 + l * 6, manaRegen: 2 + l * 0.8 }) },

  { id: "greenblob", name: "Gel Guardian", model: "creatures/greenblob", role: "warden", unlockDepth: 2,
    desc: "A stalwart blob that bolsters your maximum health.",
    grants: (l) => ({ hp: 20 + l * 10 }) },

  { id: "dragon", name: "Emberling", model: "creatures/dragon", role: "hunter", unlockDepth: 3,
    desc: "A young dragon that looses fire bolts at nearby foes.",
    hunter: (l) => ({ dmg: 6 + l * 4, cd: Math.max(0.9, 2.2 - l * 0.12), range: 12 }) },

  { id: "mushnub", name: "Mushnub", model: "creatures/mushnub", role: "forager", unlockDepth: 4,
    desc: "Sniffs out fortune — rarer drops and more relics from the depths.",
    grants: (l) => ({ luck: 0.06 + l * 0.02 }) },

  { id: "cat", name: "Vault Cat", model: "creatures/cat", role: "duelist", unlockDepth: 5,
    desc: "An uncanny familiar that sharpens your critical strikes.",
    grants: (l) => ({ crit: 0.04 + l * 0.012 }) },
];

const byId = Object.fromEntries(PETS.map((p) => [p.id, p]));
export const getPet = (id) => byId[id] || PETS[0];
